'use client';

/**
 * TestingExplorer - ARUN_ Analysis Runs browser
 *
 * Lists architecture analysis runs. Click to scope the coverage
 * graph canvas and copilot panels to that run.
 *
 * @layer presentation
 * @arch_component_type explorer
 * @arch_domain testing
 */

import { useState, useEffect } from 'react';
import {
  FlaskConical,
  Loader2,
  RefreshCw,
  Network,
  Package,
  TestTube2,
  Calendar,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils/cn';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';
import { api } from '@/lib/api/client';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AnalysisRun {
  run_id: string;
  created_at: string | null;
  node_count: number;
  package_count: number;
  system_count: number;
  tests_linked: number;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function TestingExplorer() {
  const [runs, setRuns] = useState<AnalysisRun[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [syncing, setSyncing] = useState(false);

  const selectNode = useGraphSelectionStore((s) => s.selectNode);
  const selectedNode = useGraphSelectionStore((s) => s.selectedNode);

  const fetchRuns = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await api.get<AnalysisRun[]>('/testing/runs/analysis');
      setRuns(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load analysis runs');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchRuns();
  }, []);

  const handleSync = async () => {
    setSyncing(true);
    try {
      await api.post('/testing/sync');
      await fetchRuns();
    } catch {
      // Sync error — silent, user sees no change
    } finally {
      setSyncing(false);
    }
  };

  const handleSelectRun = (run: AnalysisRun) => {
    selectNode({
      id: run.run_id,
      type: 'analysis_run',
      label: run.run_id,
      properties: run as unknown as Record<string, unknown>,
    });
  };

  // Filter runs
  const filteredRuns = runs?.filter((r) => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return r.run_id.toLowerCase().includes(q);
  });

  // Format date for display
  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Unknown';
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  const truncateRunId = (runId: string) => {
    if (runId.length <= 16) return runId;
    return `${runId.slice(0, 12)}...`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <div className="text-sm text-destructive mb-2">{error}</div>
        <Button size="sm" variant="outline" onClick={fetchRuns}>
          <RefreshCw className="w-3 h-3 mr-1" />
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-3 border-b border-border space-y-2">
        <div className="flex items-center gap-2">
          <Input
            placeholder="Filter runs..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="h-8 text-sm"
          />
          <Button
            size="sm"
            variant="outline"
            onClick={fetchRuns}
            disabled={isLoading}
            className="h-8 px-2"
          >
            <RefreshCw className={cn('w-3 h-3', isLoading && 'animate-spin')} />
          </Button>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={handleSync}
          disabled={syncing}
          className="w-full h-8"
        >
          {syncing ? (
            <>
              <Loader2 className="w-3 h-3 mr-1 animate-spin" />
              Syncing...
            </>
          ) : (
            <>
              <RefreshCw className="w-3 h-3 mr-1" />
              Sync Tests
            </>
          )}
        </Button>
      </div>

      {/* Runs List */}
      <div className="flex-1 overflow-auto">
        {!filteredRuns || filteredRuns.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-8 text-muted-foreground">
            <FlaskConical className="w-10 h-10 mb-3 opacity-30" />
            <p className="text-sm font-medium">No analysis runs</p>
            <p className="text-xs mt-1">
              {filter ? 'No matches for filter' : 'Run architecture sync first'}
            </p>
          </div>
        ) : (
          <div className="py-1">
            {filteredRuns.map((run) => {
              const isSelected = selectedNode?.id === run.run_id;

              return (
                <div
                  key={run.run_id}
                  className={cn(
                    'flex flex-col gap-1 px-3 py-2.5 cursor-pointer',
                    'hover:bg-accent/50 transition-colors',
                    isSelected && 'bg-accent'
                  )}
                  onClick={() => handleSelectRun(run)}
                >
                  {/* Run ID + Date */}
                  <div className="flex items-center gap-2">
                    <Network className="w-4 h-4 shrink-0 text-blue-400" />
                    <span className="text-sm font-medium font-mono truncate" title={run.run_id}>
                      {truncateRunId(run.run_id)}
                    </span>
                  </div>

                  {/* Stats row */}
                  <div className="flex items-center gap-3 ml-6 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1" title="Date">
                      <Calendar className="w-3 h-3" />
                      {formatDate(run.created_at)}
                    </span>
                    <span className="flex items-center gap-1" title="Nodes">
                      <Package className="w-3 h-3" />
                      {run.node_count}
                    </span>
                    <span className="flex items-center gap-1" title="Tests linked">
                      <TestTube2 className="w-3 h-3" />
                      {run.tests_linked}
                    </span>
                  </div>

                  {/* Badges */}
                  <div className="flex items-center gap-1.5 ml-6">
                    <Badge variant="secondary" className="text-[10px] px-1.5">
                      {run.package_count} pkg
                    </Badge>
                    {run.tests_linked > 0 && (
                      <Badge variant="default" className="text-[10px] px-1.5">
                        {run.tests_linked} tests
                      </Badge>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
